import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { APISource, Dataset } from '@/lib/types';

interface GlobalSearchProps {
  sources: APISource[];
  datasets: Dataset[];
  onNavigate: (tab: string, filter?: any) => void;
  placeholder?: string;
}

interface SearchResult {
  type: 'source' | 'dataset' | 'category' | 'action';
  id: string;
  title: string;
  description: string;
  action: () => void;
  category?: string;
  keywords?: string[];
}

export function GlobalSearch(props: Readonly<GlobalSearchProps>) {
  const {
    sources,
    datasets,
    onNavigate,
    placeholder = 'Search datasets, sources, or actions...',
  } = props;
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);

  // Generate search results based on query
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);

      return;
    }

    const searchResults: SearchResult[] = [];
    const lowerQuery = query.toLowerCase();

    // Search actions
    const actions = [
      {
        id: 'analyze-correlations',
        title: 'Analyze Correlations',
        description: 'Find relationships between datasets',
        tab: 'correlations',
      },
      {
        id: 'check-quality',
        title: 'Check Data Quality',
        description: 'Validate data quality and completeness',
        tab: 'quality',
      },
      {
        id: 'create-visualizations',
        title: 'Create Visualizations',
        description: 'Build charts and graphs',
        tab: 'visualize',
      },
      {
        id: 'export-data',
        title: 'Export Data',
        description: 'Save your work for external tools',
        tab: 'export',
      },
      {
        id: 'transform-data',
        title: 'Transform Data',
        description: 'Clean and prepare data',
        tab: 'transform',
      },
      {
        id: 'browse-templates',
        title: 'Browse Templates',
        description: 'Start with research templates',
        tab: 'templates',
      },
    ];

    actions.forEach(action => {
      if (
        action.title.toLowerCase().includes(lowerQuery) ||
        action.description.toLowerCase().includes(lowerQuery)
      ) {
        searchResults.push({
          type: 'action',
          id: action.id,
          title: action.title,
          description: action.description,
          action: () => onNavigate(action.tab),
        });
      }
    });

    // Search data sources
    sources.forEach(source => {
      if (
        source.name.toLowerCase().includes(lowerQuery) ||
        source.category.toLowerCase().includes(lowerQuery) ||
        source.description?.toLowerCase().includes(lowerQuery)
      ) {
        searchResults.push({
          type: 'source',
          id: source.id,
          title: source.name,
          description: `${source.category} • ${source.datasets.length} datasets`,
          category: source.category,
          action: () => onNavigate('discover', { sourceId: source.id }),
        });
      }
    });

    // Search datasets
    datasets.forEach(dataset => {
      if (
        dataset.name.toLowerCase().includes(lowerQuery) ||
        dataset.description.toLowerCase().includes(lowerQuery) ||
        dataset.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
      ) {
        searchResults.push({
          type: 'dataset',
          id: dataset.id,
          title: dataset.name,
          description: dataset.description,
          keywords: dataset.tags || [],
          action: () => onNavigate('browse-all', { datasetId: dataset.id }),
        });
      }
    });

    // Search categories
    const categories = [...new Set(sources.map(s => s.category))];

    categories.forEach(category => {
      if (category.toLowerCase().includes(lowerQuery)) {
        searchResults.push({
          type: 'category',
          id: category,
          title: `${category} Data Sources`,
          description: `Browse all ${category.toLowerCase()} datasets`,
          category,
          action: () => onNavigate('discover', { category }),
        });
      }
    });

    // Sort results by relevance
    searchResults.sort((a, b) => {
      const aScore = getRelevanceScore(a, lowerQuery);
      const bScore = getRelevanceScore(b, lowerQuery);

      return bScore - aScore;
    });

    setResults(searchResults.slice(0, 10)); // Limit to top 10 results
  }, [query, sources, datasets, onNavigate]);

  const getRelevanceScore = (result: SearchResult, query: string): number => {
    let score = 0;

    // Exact title match gets highest score
    if (result.title.toLowerCase() === query) {
      score += 100;
    } else if (result.title.toLowerCase().startsWith(query)) {
      score += 50;
    } else if (result.title.toLowerCase().includes(query)) {
      score += 25;
    }

    // Description match
    if (result.description.toLowerCase().includes(query)) {
      score += 10;
    }

    // Keywords match
    if (result.keywords?.some(k => k.toLowerCase().includes(query))) {
      score += 15;
    }

    // Action type priority
    if (result.type === 'action') {
      score += 20;
    }

    return score;
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'source':
        return '🗂️';
      case 'dataset':
        return '📊';
      case 'category':
        return '📁';
      case 'action':
        return '⚡';
      default:
        return '🔍';
    }
  };

  const handleSelect = (result: SearchResult) => {
    result.action();
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div className='relative w-full max-w-md'>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className='relative'>
            <MagnifyingGlassIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
            <Input
              type='text'
              placeholder={placeholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setIsOpen(true)}
              className='pl-10 pr-10'
            />
            {query && (
              <Button
                variant='ghost'
                size='sm'
                className='absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0'
                onClick={() => {
                  setQuery('');
                  setIsOpen(false);
                }}
              >
                <XIcon className='w-3 h-3' />
              </Button>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className='w-80 p-0' align='start'>
          <Command>
            <CommandList>
              {results.length === 0 && query ? (
                <CommandEmpty>
                  <div className='text-center py-6'>
                    <MagnifyingGlassIcon className='w-8 h-8 mx-auto mb-2 text-muted-foreground' />
                    <p className='text-sm text-muted-foreground'>
                      No results found for "{query}"
                    </p>
                  </div>
                </CommandEmpty>
              ) : (
                <>
                  {/* Group results by type */}
                  {['action', 'source', 'dataset', 'category'].map(type => {
                    const typeResults = results.filter(r => r.type === type);

                    if (typeResults.length === 0) {
                      return null;
                    }

                    const typeLabels = {
                      action: 'Actions',
                      source: 'Data Sources',
                      dataset: 'Datasets',
                      category: 'Categories',
                    };

                    return (
                      <CommandGroup
                        key={type}
                        heading={typeLabels[type as keyof typeof typeLabels]}
                      >
                        {typeResults.map(result => (
                          <CommandItem
                            key={result.id}
                            value={result.title}
                            onSelect={() => handleSelect(result)}
                            className='flex items-start gap-3 p-3 cursor-pointer'
                          >
                            <span className='text-lg'>
                              {getResultIcon(result.type)}
                            </span>
                            <div className='flex-1 min-w-0'>
                              <div className='font-medium text-sm truncate'>
                                {result.title}
                              </div>
                              <div className='text-xs text-muted-foreground truncate'>
                                {result.description}
                              </div>
                              {result.keywords &&
                                result.keywords.length > 0 && (
                                  <div className='flex gap-1 mt-1'>
                                    {result.keywords
                                      .slice(0, 3)
                                      .map(keyword => (
                                        <Badge
                                          key={keyword}
                                          variant='secondary'
                                          className='text-xs'
                                        >
                                          {keyword}
                                        </Badge>
                                      ))}
                                  </div>
                                )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  })}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
